import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  EmptyIndicator,
  FlexContainer,
  Link,
  MoreOptions,
  Pagination,
  Search,
  TimeIndicator,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow,
  BoxTitleWithSelectedItems
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import { UsersSendMessage } from 'components/users';

import type { PageState } from 'types';

const MyTeamUsers = () => {
  const { language, messages, serverConfig, theme, user } = useReduxState();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, loading } = useApiData({
    url:
      `/admin/users-admin` +
      `?supervisorId=${user.id}` +
      `&page=${page}` +
      `&search=${search}` +
      `&includeCompletedContent=true`
  });

  const handleSearch = async ({ search }) => {
    setPage(1);
    setSearch(search);
  };

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  const cellStyle = {
    color: theme.textColorLight,
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.2
  };

  return (
    <>
      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <div style={{ width: 240 }}>
              <Search
                onSearch={handleSearch}
                placeholder={messages.settingsSearchForUser}
                size="header"
              />
            </div>

            <UsersSendMessage
              buttonSize="header"
              id="my-team-users-header-send-message"
              userSelectionParams={{
                excludeUsersWithoutEmail: true,
                routerQueryParams: { supervisorId: `${user.id}` },
                search,
                userSelector: 'emails'
              }}
            />
          </FlexContainer>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={
          <BoxTitleWithSelectedItems
            numberOfItems={data?.usersCount || 0}
            title={messages.adminSupervisorsSupervisees}
          />
        }
      >
        {data?.users?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonUser}</BoxTableCell>

                <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>

                {coursesVisible && (
                  <BoxTableCell>
                    <div data-tooltip={messages.usersCompletedCoursesTooltip}>
                      {messages.usersCompletedCourses}
                      <Icon
                        icon="question-circle"
                        style={{
                          fontSize: '1.1rem',
                          marginLeft: 5,
                          position: 'relative',
                          top: -1
                        }}
                      />
                    </div>
                  </BoxTableCell>
                )}

                {pathsVisible && (
                  <BoxTableCell>{messages.usersCompletedPaths}</BoxTableCell>
                )}

                {achievementsVisible && (
                  <BoxTableCell>
                    {messages.usersCompletedAchievements}
                  </BoxTableCell>
                )}

                <BoxTableCell shrink={true}></BoxTableCell>
              </BoxTableHeader>

              {data.users.map((user: PageState['user']) => (
                <BoxTableRow key={user.id}>
                  <BoxTableCell>
                    <User enableProfileLink={true} user={user} />
                  </BoxTableCell>

                  <BoxTableCell emptyValue="-">
                    {user.lastLogin ? (
                      <span
                        aria-label={getFormattedDate({ date: user.lastLogin })}
                        data-tooltip={getFormattedDate({
                          date: user.lastLogin
                        })}
                        style={{ textTransform: 'lowercase' }}
                      >
                        <TimeIndicator
                          language={language}
                          timeString={user.lastLogin as string}
                        />
                      </span>
                    ) : null}
                  </BoxTableCell>

                  {coursesVisible && (
                    <BoxTableCell>
                      {user?.completedData?.courses ? (
                        <Link
                          as={`/users/${user.id}/courses`}
                          href="/users/[userId]/courses"
                          text={user?.completedData?.courses}
                          style={cellStyle}
                        />
                      ) : null}
                    </BoxTableCell>
                  )}

                  {pathsVisible && (
                    <BoxTableCell>
                      {user?.completedData?.paths ? (
                        <Link
                          as={`/users/${user.id}/paths`}
                          href="/users/[userId]/paths"
                          text={user?.completedData?.paths}
                          style={cellStyle}
                        />
                      ) : null}
                    </BoxTableCell>
                  )}

                  {achievementsVisible && (
                    <BoxTableCell>
                      {user?.completedData?.achievements ? (
                        <Link
                          as={`/users/${user.id}/achievements`}
                          href="/users/[userId]/achievements"
                          text={user?.completedData?.achievements}
                          style={cellStyle}
                        />
                      ) : null}
                    </BoxTableCell>
                  )}

                  <BoxTableCell>
                    {user.email ? (
                      <MoreOptions renderAsFixed={true}>
                        <UsersSendMessage
                          id="my-team-users-user-send-message"
                          userSelectionParams={{
                            selectedUserIds: [user.id],
                            userSelector: 'emails'
                          }}
                        />
                      </MoreOptions>
                    ) : null}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />
    </>
  );
};

export default MyTeamUsers;
